
import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense, lazy } from 'react';
import { 
    Product, ProductCategory, NewsItem, NewsCategory, ReviewCategory, Subscriber, Banner, Tip, Popup, User, SiteSettings, 
    ProductFormData, NewsFormData, BannerFormData, TipFormData, PopupFormData, AdminUser, KeywordLink, InternalLinkLog, CustomPage, AffiliateLink, Business, GuideCategory,
    BioLink, BioLinkFormData, InformativeCard, ScriptAd, Plan, Subscription
} from './types';
import { API_URL, getBasePath, getArticleUrl } from './constants';

// --- COMPONENTS ---
import Header from './components/Header';
import Hero from './components/Hero';
import ProductCarousel from './components/ProductCarousel';
import NewsSection from './components/NewsSection';
import AuthModal from './components/AuthModal';
import AiChatWidget from './components/AiChatWidget';
import WhatsappFloatingButton from './components/WhatsappFloatingButton';
import AdBlockDetector from './components/AdBlockDetector';
import Footer from './components/Footer';
import MobileBottomNav from './components/MobileBottomNav';
import AdvancedSearch from './components/AdvancedSearch';
import NotFoundPage from './components/NotFoundPage';
import PopupDisplay from './components/PopupDisplay';
import CategorySlider from './components/CategorySlider';
import SEOManager from './components/SEOManager';
import { Helmet } from './components/Helmet';
import { AlertTriangle } from 'lucide-react';
import LicenseGuard from './components/LicenseGuard';
import AdSenseScript from './components/AdSenseScript';
import TrackingScripts from './components/TrackingScripts';
import GlobalAdScript from './components/GlobalAdScript';
import PwaHandler, { BeforeInstallPromptEvent } from './components/PwaHandler';
import { ErrorBoundary } from './components/ErrorBoundary';

import { Toaster, toast } from 'sonner';
import { slugify } from './src/lib/utils';

// --- LAZY COMPONENTS (Code Splitting and Route Optimization) ---
const ArticlePage = lazy(() => import('./components/ArticlePage'));
const ProductsPage = lazy(() => import('./components/ProductsPage'));
const BlogPage = lazy(() => import('./components/BlogPage'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const BeneficiosPage = lazy(() => import('./components/BeneficiosPage'));
const ContactPage = lazy(() => import('./components/ContactPage'));
const LegalPage = lazy(() => import('./components/LegalPage'));
const CustomPageRenderer = lazy(() => import('./components/CustomPageRenderer'));
const WebStoriesPage = lazy(() => import('./components/WebStoriesPage'));
const DirectoryPage = lazy(() => import('./components/DirectoryPage'));
const DirectorySearchResults = lazy(() => import('./components/DirectorySearchResults'));
const BusinessDetailPage = lazy(() => import('./components/BusinessDetailPage'));
const ProductDetailPage = lazy(() => import('./components/ProductDetailPage'));
const WebStoryViewer = lazy(() => import('./components/WebStoryViewer'));
const BioLinkPage = lazy(() => import('./components/BioLinkPage'));
const PublicSitePage = lazy(() => import('./components/PublicSitePage'));
const GamificationMissionViewer = lazy(() => import('./components/GamificationMissionViewer'));

const Dashboard = lazy(() => import('./components/Dashboard'));
const CheckoutFlow = lazy(() => import('./components/CheckoutFlow'));
const CarouselApp = lazy(() => import('./components/carousel-module/CarouselApp'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));

const getCachedData = () => {
    try {
        const cached = localStorage.getItem('cached_api_data');
        return cached ? JSON.parse(cached) : null;
    } catch (e) {
        console.error("Failed to parse cached api data:", e);
        return null;
    }
};
const cachedData = getCachedData();

const App = () => {
    const [loading, setLoading] = useState(() => {
        try {
            return !localStorage.getItem('cached_api_data');
        } catch (e) {
            return true;
        }
    });
    const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
    const [apiError, setApiError] = useState<string | null>(null);
    const [plans, setPlans] = useState<Plan[]>(() => cachedData?.plans || []);
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    
    // PWA Logic
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [canInstallPwa, setCanInstallPwa] = useState(false);
    const [isPwaInstalled, setIsPwaInstalled] = useState(false);

    useEffect(() => {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                           (window.navigator as any).standalone || 
                           document.referrer.includes('android-app://');
        
        setIsPwaInstalled(isStandalone);

        const handleBeforeInstallPrompt = (e: any) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setCanInstallPwa(true);
        };

        const handleAppInstalled = () => {
            setDeferredPrompt(null);
            setCanInstallPwa(false);
            setIsPwaInstalled(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallPwa = async () => {
        // 1. Check if we are inside a preview iframe or sandboxed environment
        const isIframe = window.self !== window.top;
        if (isIframe) {
            toast.promise(
                new Promise((resolve) => setTimeout(resolve, 1500)),
                {
                    loading: 'Abrindo o aplicativo em nova aba para instalação nativa...',
                    success: () => {
                        window.open(window.location.origin, '_blank');
                        return 'Abra em tela cheia na nova aba para instalar instantaneamente!';
                    },
                    error: 'Não foi possível redirecionar automaticamente.'
                }
            );
            return;
        }

        const ua = navigator.userAgent.toLowerCase();
        const isIos = /iphone|ipad|ipod/.test(ua);

        // 2. iOS Direct Profile Installation Bypass (Creates Native Icon on Home Screen automatically)
        if (isIos) {
            toast.loading('Iniciando Instalador Nativo do iOS...', {
                duration: 4000,
            });
            setTimeout(() => {
                window.location.href = '/pwa-install.mobileconfig';
                toast.success('Iniciado! Toque em "Permitir" no aviso, depois vá em "Ajustes > Perfil Baixado > Instalar" no seu iPhone!', {
                    duration: 10000,
                });
            }, 1500);
            return;
        }

        // 3. Android and standard Chrome/Edge/Firefox programmatical install prompt
        if (deferredPrompt) {
            try {
                deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                if (outcome === 'accepted') {
                    setDeferredPrompt(null);
                    setCanInstallPwa(false);
                    toast.success('Pronto! O aplicativo foi instalado na sua tela inicial de aplicativos.');
                } else {
                    toast.error('Instalação cancelada.');
                }
            } catch (err) {
                console.error('PWA: Error prompting native install:', err);
                toast.error('Erro ao iniciar a instalação do aplicativo.');
            }
        } else {
            // Standard PWA prompting
            if (/android/.test(ua)) {
                toast.info('Instalação nativa iniciada! Confirme no aviso "Adicionar à Tela Principal" ou use o menu flutuante (três pontos) do Google Chrome.', {
                    duration: 8000,
                });
            } else {
                toast.info('Para instalar no Computador: Clique no ícone de download/instalação (+) presente na barra de endereços do seu navegador.', {
                    duration: 8000,
                });
            }
        }
    };
    
    const hasInitialLoadFinished = useRef(false);

    const [products, setProducts] = useState<Product[]>(() => cachedData?.products || []);
    const [news, setNews] = useState<NewsItem[]>(() => {
        const rawNews = cachedData?.news || [];
        return rawNews.map((n: any) => {
            ['title', 'excerpt', 'content'].forEach(field => {
                if (n[field] && typeof n[field] === 'object' && n[field].text) {
                    n[field] = n[field].text;
                }
            });
            if (n.layout_type === 'recipe' && n.recipe_data) {
                if (typeof n.recipe_data === 'string') {
                    try {
                        n.recipe_data = JSON.parse(n.recipe_data);
                    } catch (e) {}
                }
                if (n.recipe_data && typeof n.recipe_data === 'object') {
                    const rd = n.recipe_data;
                    if (rd.ingredients && Array.isArray(rd.ingredients)) {
                        rd.ingredients = rd.ingredients.map((sec: any) => ({
                            ...sec,
                            title: sec.title && typeof sec.title === 'object' ? sec.title.text : (sec.sectionTitle && typeof sec.sectionTitle === 'object' ? sec.sectionTitle.text : (sec.title || sec.sectionTitle)),
                            items: Array.isArray(sec.items) ? sec.items.map((item: any) => {
                                if (typeof item === 'string') {
                                    return { id: Math.random().toString(36).substr(2, 9), text: item };
                                }
                                let text = item.text;
                                if (item.text && typeof item.text === 'object' && item.text.text) {
                                    text = item.text.text;
                                } else if (!item.text && (item.amount || item.name)) {
                                    text = `${item.amount || ''} ${item.unit || ''} ${item.name || ''}`.trim();
                                }
                                return { 
                                    ...item, 
                                    id: item.id || Math.random().toString(36).substr(2, 9),
                                    text: text || '' 
                                };
                            }) : []
                        }));
                    }
                    if (rd.instructions && Array.isArray(rd.instructions)) {
                        rd.instructions = rd.instructions.map((step: any) => {
                            let content = step.content;
                            if (step.content && typeof step.content === 'object' && step.content.text) {
                                content = step.content.text;
                            } else if (!step.content && step.text) {
                                content = typeof step.text === 'object' ? step.text.text : step.text;
                            }
                            return {
                                ...step,
                                title: step.title && typeof step.title === 'object' ? step.title.text : step.title,
                                content: content || ''
                            };
                        });
                    }
                    if (rd.chefTips && Array.isArray(rd.chefTips)) {
                        rd.chefTips = rd.chefTips.map((tip: any) => {
                            if (typeof tip === 'string') return tip;
                            if (tip && typeof tip === 'object' && tip.text) return tip.text;
                            return String(tip);
                        });
                    }
                }
            }
            if (n.layout_type === 'bible_study' && n.bible_study_data) {
                if (typeof n.bible_study_data === 'string') {
                    try {
                        n.bible_study_data = JSON.parse(n.bible_study_data);
                    } catch (e) {}
                }
            }
            return n;
        });
    });
    const [businesses, setBusinesses] = useState<Business[]>(() => cachedData?.businesses || []);
    const [newsCategories, setNewsCategories] = useState<NewsCategory[]>(() => (cachedData?.newsCategories || []).map((c: any) => ({ ...c, id: String(c.id), parentId: c.parentId ? String(c.parentId) : null })));
    const [reviewCategories, setReviewCategories] = useState<ReviewCategory[]>(() => (cachedData?.reviewCategories || []).map((c: any) => ({ ...c, id: String(c.id) })));
    const [guideCategories, setGuideCategories] = useState<GuideCategory[]>(() => (cachedData?.guideCategories || []).map((c: any) => ({ ...c, id: String(c.id) })));
    const [productCategories, setProductCategories] = useState<ProductCategory[]>(() => (cachedData?.productCategories || []).map((c: any) => ({ ...c, id: String(c.id) })));
    const [subscribers, setSubscribers] = useState<Subscriber[]>(() => cachedData?.subscribers || []);
    const [banners, setBanners] = useState<Banner[]>(() => cachedData?.banners || []);
    const [tips, setTips] = useState<Tip[]>(() => cachedData?.tips || []);
    const [popups, setPopups] = useState<Popup[]>(() => cachedData?.popups || []);
    const [users, setUsers] = useState<User[]>(() => cachedData?.users || []);
    const [customPages, setCustomPages] = useState<CustomPage[]>(() => cachedData?.customPages || []);
    const [bioLinks, setBioLinks] = useState<BioLink[]>(() => cachedData?.bioLinks || []);
    const [informativeCards, setInformativeCards] = useState<InformativeCard[]>(() => cachedData?.informativeCards || []);
    const [scriptAds, setScriptAds] = useState<ScriptAd[]>(() => cachedData?.scriptAds || []);
    const [affiliateLinks, setAffiliateLinks] = useState<AffiliateLink[]>(() => cachedData?.affiliateLinks || []);
    const [subscriptions, setSubscriptions] = useState<Subscription[]>(() => cachedData?.subscriptions || []);
    const [authorProfile, setAuthorProfile] = useState<User | null>(() => cachedData?.authorProfile || null);
    
    // Memoized public news (filtered from draft and local SEO)
    const publicNews = useMemo(() => {
        return news.filter(n => {
            const isLocalSeoFlag = n.is_local_seo === true || Number(n.is_local_seo) === 1 || String(n.is_local_seo) === '1';
            const hasLocalSeoTag = n.tags?.some(tag => {
                const t = tag.toLowerCase().trim();
                return t === 'local-seo' || t === 'seo-local' || t === 'seo local' || t === 'seo_local' || t === 'cluster' || t === 'pilar';
            });
            const isLocalSeoCategory = n.category?.toLowerCase().trim() === 'seo local' || n.category?.toLowerCase().trim() === 'local seo';
            
            return n.status !== 'draft' && !isLocalSeoFlag && !hasLocalSeoTag && !isLocalSeoCategory;
        });
    }, [news]);

    const publicCategories = useMemo(() => {
        const publicCatNames = new Set(publicNews.map(n => n.category));
        return newsCategories.filter(cat => publicCatNames.has(cat.name));
    }, [newsCategories, publicNews]);
    
    // Memoized public businesses (filtered by status and expiration)
    const publicBusinesses = useMemo(() => {
      return businesses.filter(b => {
          const isPublished = b.status === 'published';
          const isNotExpired = !b.expires_at || new Date(b.expires_at).getTime() > Date.now();
          // Seeded or manually registered businesses default to payment_status = 'pending'.
          // Therefore, they are shown as long as they are published and not expired, only filtering out explicitly 'expired' payments.
          const isNotUnpaid = b.payment_status !== 'expired';
          return isPublished && isNotExpired && isNotUnpaid;
      });
    }, [businesses]);
    
    const [settings, setSettings] = useState<SiteSettings>(() => {
        const defaultSettings: SiteSettings = {
            googleApiKey: '', openaiApiKey: '', deepseekApiKey: '', groqApiKey: '', aiProvider: 'gemini',
            primaryColor: '#2563eb', enableAdSense: false, footerBgColor: '#0f172a',
            footerTitleColor: '#ffffff', footerTextColor: '#94a3b8', footerLogoDescription: 'Desenvolvendo soluções digitais inteligentes.',
            footerShowNewsletter: true, privacyPageTitle: 'Política de Privacidade', termsPageTitle: 'Termos de Uso',
            keywordLinks: [], internalLinkLogs: [], useImageBrain: false, enableDirectory: true, directoryAsHomePage: false,
            googleSiteVerification: '', enableCustomHero: false
        };
        if (!cachedData?.settings) return defaultSettings;
        const cleanSettings = { ...defaultSettings, ...cachedData.settings };
        if (cleanSettings.installationUrl) {
            cleanSettings.installationUrl = cleanSettings.installationUrl.replace(/\/admin(?:\/.*)?$/, '').replace(/\/$/, '');
            if (!cleanSettings.installationUrl.endsWith('/')) {
                cleanSettings.installationUrl += '/';
            }
        }
        if (cleanSettings.enableDirectory === undefined) {
            cleanSettings.enableDirectory = true;
        }
        if (cleanSettings.directoryAsHomePage === undefined) {
            cleanSettings.directoryAsHomePage = false;
        }
        if (!cleanSettings.aiReviewPrompt) {
            cleanSettings.aiReviewPrompt = '';
        }
        return cleanSettings;
    });

    const getInitialStorage = (key: string, fallback: string = 'null') => {
        try {
            return localStorage.getItem(key) || fallback;
        } catch (e) {
            console.error(`Error accessing localStorage for ${key}:`, e);
            return fallback;
        }
    };

    const initialUserStr = getInitialStorage('admin_user');
    let initialUser = null;
    try {
        initialUser = JSON.parse(initialUserStr);
    } catch (e) {
        console.error("Failed to parse initial user:", e);
    }

    const [isAdmin, setIsAdmin] = useState(initialUser?.role === 'admin');
    const [adminToken, setAdminToken] = useState<string>(getInitialStorage('admin_token', ''));
    const [currentUser, setCurrentUser] = useState<AdminUser | null>(initialUser);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);
    const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);

    const params = new URLSearchParams(window.location.search);
    const [pageParam, setPageParam] = useState(params.get('pagina') || 'home');
    const [articleSlug, setArticleSlug] = useState(params.get('artigo') || null);
    const [productSlug, setProductSlug] = useState<string | null>(null);
    const [categorySlug, setCategorySlug] = useState(params.get('categoria') || null);
    const [customPageSlug, setCustomPageSlug] = useState(params.get('p') || null);
    const [bioLinkSlug, setBioLinkSlug] = useState<string | null>(null);
    const [storySlug, setStorySlug] = useState<string | null>(null);
    const [siteSlug, setSiteSlug] = useState<string | null>(null);
    const [adminTab, setAdminTab] = useState('overview');
    const [currentPath, setCurrentPath] = useState(window.location.pathname);
    const [directorySearchWhat, setDirectorySearchWhat] = useState(params.get('q') || '');
    const [directorySearchWhere, setDirectorySearchWhere] = useState(params.get('loc') || '');

    useEffect(() => {
        const uParams = new URLSearchParams(window.location.search);
        const refCode = uParams.get('ref') || uParams.get('code');
        if (refCode) {
            localStorage.setItem('pending_referral_code', refCode);
            console.log("Captured persistent referral code:", refCode);
        }
    }, []);

    useEffect(() => {
        const handlePopState = () => {
            setCurrentPath(window.location.pathname);
        };

        // Interceptor para links internos gerados pela IA ou no conteúdo
        const handleInternalLinks = (e: MouseEvent) => {
            // Ignorar cliques com botão direito ou com modificadores
            if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
            
            const target = e.target as HTMLElement;
            const link = target.closest('a');
            
            if (link && link.hasAttribute('href')) {
                const rawHref = link.getAttribute('href') || '';
                
                // Ignora âncoras locais ou vazias
                if (rawHref.startsWith('#') || rawHref === '') {
                     if (rawHref.startsWith('#') && rawHref !== '#') {
                         return; // Deixa rolar pra âncora
                     }
                     return;
                }
                
                // Se for um link externo, deixa o navegador abrir
                if (rawHref.startsWith('http') && !rawHref.startsWith(window.location.origin)) {
                     return;
                }
                
                // Target ou download escapa
                if (link.target || link.hasAttribute('download')) return;
                
                const base = getBasePath();
                const rootBase = base.startsWith('/') ? base : `/${base}`;
                const rootBaseClean = rootBase.endsWith('/') ? rootBase : `${rootBase}/`;

                let finalUrl = link.href;

                // Aqui está o "pulo do gato" para resolver links internos quebrados em páginas profundas:
                // Se o link origiado for puramente "contato" ou "slug-do-artigo" (sem barra inicial e sem http)
                // O navegador tentaria somar com a rota profunda (ex: /produtos/desenvolvimento-web/contato)
                // Nós interceptamos e forçamos o cálculo a partir da raiz do applet.
                if (!rawHref.startsWith('/') && !rawHref.startsWith('http')) {
                    // Evita dupla adição da base caso o navegador já tenha resolvido certo se estávamos na home
                    finalUrl = window.location.origin + rootBaseClean + rawHref;
                }
                
                const url = new URL(finalUrl, window.location.origin);
                const path = url.pathname;
                
                // Evitar interceptar links da API
                if (path.includes('/api/')) return;
                
                // Evitar extensões de arquivo nativas (download direto)
                const ext = path.split('.').pop()?.toLowerCase();
                if (ext && ['pdf','zip','png','jpg','jpeg','gif','xml'].includes(ext)) {
                     return;
                }

                e.preventDefault();
                
                // Push no history api
                window.history.pushState({}, '', finalUrl);
                
                // Forçar verificação de rota pelo useEffect
                setCurrentPath(window.location.pathname);
                const navEvent = new PopStateEvent('popstate');
                window.dispatchEvent(navEvent);
                
                window.scrollTo(0, 0);
            }
        };

        window.addEventListener('popstate', handlePopState);
        document.addEventListener('click', handleInternalLinks);
        return () => {
            window.removeEventListener('popstate', handlePopState);
            document.removeEventListener('click', handleInternalLinks);
        };
    }, []);

    useEffect(() => {
        window.dispatchEvent(new Event('app-route-changed'));
    }, [currentPath]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setDirectorySearchWhat(params.get('q') || '');
        setDirectorySearchWhere(params.get('loc') || '');

        const path = window.location.pathname;
        const basePath = getBasePath();
        
        // Remove basePath from path to get the actual route
        let routePath = path;
        if (basePath && path.startsWith(basePath)) {
            routePath = path.substring(basePath.length);
        }
        
        const pathParts = routePath.split('/').filter(p => p !== '' && p !== 'index.php');
        
        if (pathParts.length === 0) {
            const page = params.get('pagina');
            if (page) {
                setPageParam(page);
                setArticleSlug(params.get('artigo') || null);
                setCategorySlug(params.get('categoria') || null);
                setCustomPageSlug(params.get('p') || null);
                return;
            }
            setPageParam(settings.directoryAsHomePage ? 'guia' : 'home');
            setArticleSlug(null);
            setCategorySlug(null);
            setCustomPageSlug(null);
            return;
        }

        // Check for custom pages
        if (pathParts[0] === 'p' && pathParts[1]) {
            setPageParam('p');
            setCustomPageSlug(pathParts[1]);
            setArticleSlug(null);
            setCategorySlug(null);
            return;
        }

        // Check for bio links
        if (pathParts[0] === 'bio' && pathParts[1]) {
            setPageParam('bio');
            setBioLinkSlug(pathParts[1]);
            setArticleSlug(null);
            setCategorySlug(null);
            setCustomPageSlug(null);
            return;
        }

        // Check for generated sites (/site/slug)
        if (pathParts[0] === 'site' && pathParts[1]) {
            setPageParam('site');
            setSiteSlug(pathParts[1]);
            setArticleSlug(null);
            setCategorySlug(null);
            setCustomPageSlug(null);
            return;
        }

        // Check for webstories
        if (pathParts[0] === 'webstories') {
            setPageParam('webstories');
            setArticleSlug(null);
            setCategorySlug(null);
            setCustomPageSlug(null);
            setStorySlug(null);
            return;
        }

        // Check for individual web story
        if (pathParts[0] === 'story' && pathParts[1]) {
            setPageParam('story');
            setStorySlug(pathParts[1]);
            setArticleSlug(null);
            setCategorySlug(null);
            setCustomPageSlug(null);
            return;
        }

        // Check for directory (/guia or /guia/category/slug)
        if (pathParts[0] === 'guia') {
            setPageParam('guia');
            if (pathParts[1]) {
                setCategorySlug(decodeURIComponent(pathParts[1]));
            } else {
                setCategorySlug(null);
            }
            if (pathParts.length > 2) {
                setArticleSlug(decodeURIComponent(pathParts[pathParts.length - 1]));
            } else {
                setArticleSlug(null);
            }
            setCustomPageSlug(null);
            return;
        }

        // Check for admin
        if (pathParts[0] === 'admin') {
            // Clean up multiple 'admin' segments caused by previous routing bug
            const cleanParts = pathParts.filter((part, index) => part !== 'admin' || index === 0);
            
            // If it's just /admin or /admin/tab, it's the dashboard
            if (cleanParts.length <= 2) {
                const targetTab = cleanParts[1] || 'overview';
                setPageParam('admin');
                setAdminTab(targetTab);
                
                // Fix the URL if it was duplicated so we don't end up with /admin/admin/admin
                if (pathParts.length > 2 || (pathParts.length === 2 && pathParts[1] === 'admin')) {
                    const base = getBasePath();
                    const rootBase = base.startsWith('/') ? base : `/${base}`;
                    const rootBaseClean = rootBase.endsWith('/') ? rootBase : `${rootBase}/`;
                    window.history.replaceState({}, '', `${rootBaseClean}admin/${targetTab}`);
                }

                setArticleSlug(null);
                setCategorySlug(null);
                setCustomPageSlug(null);
                return;
            } else {
                // If it's /admin/tab/category/article, it's likely a broken internal link
                // We strip the admin parts and try to find the article
                const possibleCategory = cleanParts[2];
                const possibleArticle = cleanParts[3];
                if (possibleCategory && possibleArticle) {
                    setPageParam('blog');
                    setCategorySlug(decodeURIComponent(possibleCategory));
                    setArticleSlug(decodeURIComponent(possibleArticle));
                    setCustomPageSlug(null);
                    return;
                }
                // Fallback for any other malformed admin URLs
                setPageParam('admin');
                setAdminTab('overview');
                
                const base = getBasePath();
                const rootBase = base.startsWith('/') ? base : `/${base}`;
                const rootBaseClean = rootBase.endsWith('/') ? rootBase : `${rootBase}/`;
                window.history.replaceState({}, '', `${rootBaseClean}admin/overview`);
                
                setArticleSlug(null);
                setCategorySlug(null);
                setCustomPageSlug(null);
                return;
            }
        }

        // Check for affiliate links (/recomenda/slug)
        if (pathParts[0] === 'recomenda' && pathParts[1]) {
            window.location.href = `${API_URL}?action=redirect_affiliate&slug=${pathParts[1]}`;
            return;
        }

        // Check for directory (/guia or /guia/category/slug)
        if (pathParts[0] === 'guia') {
            setPageParam('guia');
            if (pathParts[1]) {
                setCategorySlug(decodeURIComponent(pathParts[1]));
            } else {
                setCategorySlug(null);
            }
            if (pathParts[2]) {
                setArticleSlug(decodeURIComponent(pathParts[2]));
            } else {
                setArticleSlug(null);
            }
            setCustomPageSlug(null);
            return;
        }

        // Check for products (/produtos or /produtos/slug)
        // Or if the first segment directly matches a product slug or id
        const firstSegmentDecoded = pathParts[0] ? decodeURIComponent(pathParts[0]) : '';
        const matchingProductDirect = products.find(p => p.slug === firstSegmentDecoded || p.id == firstSegmentDecoded);
        
        if (pathParts[0] === 'produtos' || matchingProductDirect) {
            setPageParam('produtos');
            if (pathParts[0] === 'produtos' && pathParts[1]) {
                setProductSlug(decodeURIComponent(pathParts[1]));
            } else if (matchingProductDirect) {
                setProductSlug(firstSegmentDecoded);
            } else {
                setProductSlug(null);
            }
            setArticleSlug(null);
            setCategorySlug(null);
            setCustomPageSlug(null);
            return;
        }

        // Check for legacy routes (/news/artigo/slug or /news/categoria/slug)
        if (pathParts[0] === 'news') {
            if (pathParts[1] === 'artigo' && pathParts[2]) {
                setPageParam('blog');
                setArticleSlug(decodeURIComponent(pathParts[2]));
                setCategorySlug(null);
                setCustomPageSlug(null);
                return;
            }
            if (pathParts[1] === 'categoria' && pathParts[2]) {
                setPageParam('blog');
                setCategorySlug(decodeURIComponent(pathParts[2]));
                setArticleSlug(null);
                setCustomPageSlug(null);
                return;
            }
        }

        // Check for article with /artigo/ prefix
        if (pathParts[0] === 'artigo' && pathParts[1]) {
            setPageParam('blog');
            if (pathParts[2]) {
                // /artigo/category/slug
                setCategorySlug(decodeURIComponent(pathParts[1]));
                setArticleSlug(decodeURIComponent(pathParts[2]));
            } else {
                // /artigo/slug
                setArticleSlug(decodeURIComponent(pathParts[1]));
                // Try to find the category for this article
                const article = news.find(n => n.slug === decodeURIComponent(pathParts[1]));
                if (article) {
                    const cat = newsCategories.find(c => c.name === article.category || c.slug === article.category);
                    setCategorySlug(cat ? cat.slug : 'geral');
                } else {
                    setCategorySlug(null);
                }
            }
            setCustomPageSlug(null);
            return;
        }

        // Check for category and article (/:category/:slug)
        // We exclude known static pages like 'produtos', 'contato', etc. if they are handled separately
        if (pathParts.length > 0) {
            const decodedPath = decodeURIComponent(pathParts[0]);
            
            // 1. Check if it's a known static page
            const knownPages = ['home', 'produtos', 'carrossel', 'carousel', 'contato', 'sobre', 'servicos', 'blog', 'search', 'quem-somos', 'privacidade', 'termos'];
            if (knownPages.includes(decodedPath) && !newsCategories.find(c => c.slug === decodedPath)) {
                setPageParam(decodedPath);
                setArticleSlug(null);
                setCategorySlug(null);
                setCustomPageSlug(null);
                return;
            }

            // 2. Check if it's a custom page slug (without /p/ prefix)
            const customPage = customPages.find(p => p.slug === decodedPath);
            if (customPage) {
                setPageParam('p');
                setCustomPageSlug(decodedPath);
                setArticleSlug(null);
                setCategorySlug(null);
                return;
            }

            // 3. Check if it's an article slug (intelligent routing: /produtos/slug-do-artigo)
            if (pathParts.length === 1) {
                const article = news.find(n => n.slug === decodedPath);
                if (article) {
                    setPageParam('blog');
                    setArticleSlug(decodedPath);
                    // Try to find the category slug for this article
                    const cat = newsCategories.find(c => c.name === article.category || c.slug === article.category);
                    setCategorySlug(cat ? cat.slug : 'geral');
                    setCustomPageSlug(null);
                    return;
                }
            }

            // 4. Otherwise, assume it's a category (/:category or /:category/:slug)
            setPageParam('blog');
            setCategorySlug(decodedPath);
            if (pathParts[2]) {
                setArticleSlug(decodeURIComponent(pathParts[2]));
            } else if (pathParts[1]) {
                setArticleSlug(decodeURIComponent(pathParts[1]));
            } else {
                setArticleSlug(null);
            }
            setCustomPageSlug(null);
            return;
        }
    }, [newsCategories, news, customPages, currentPath, settings.directoryAsHomePage, businesses, bioLinks, products]);

    const handleProductClick = (product: Product) => {
        const base = getBasePath();
        const rootBase = base.startsWith('/') ? base : `/${base}`;
        const rootBaseClean = rootBase.endsWith('/') ? rootBase : `${rootBase}/`;
        
        const slug = product.slug || product.id;
        window.history.pushState({}, '', `${rootBaseClean}produtos/${slug}`);
        
        setPageParam('produtos');
        setProductSlug(slug);
        setArticleSlug(null);
        setCategorySlug(null);
        setCustomPageSlug(null);
        setCurrentPath(window.location.pathname);
        window.scrollTo(0, 0);
    };

    const handleNavigate = (page: string, category?: string) => {
        const base = getBasePath();
        const rootBase = base.startsWith('/') ? base : `/${base}`;
        const rootBaseClean = rootBase.endsWith('/') ? rootBase : `${rootBase}/`;
        
        if (page === 'home') {
            window.history.pushState({}, '', rootBaseClean);
            setPageParam(settings.directoryAsHomePage ? 'guia' : 'home');
            setCategorySlug(null);
            setArticleSlug(null);
            setCustomPageSlug(null);
        } else if (page === 'admin') {
            window.history.pushState({}, '', `${rootBaseClean}admin/overview`);
            setPageParam('admin');
            setAdminTab('overview');
        } else if (page === 'webstories') {
            window.history.pushState({}, '', `${rootBaseClean}webstories`);
            setPageParam('webstories');
        } else if (page === 'guia' || page.startsWith('guia/')) {
            window.history.pushState({}, '', `${rootBaseClean}${page}`);
            setPageParam('guia');
            if (page === 'guia') {
                setDirectorySearchWhat('');
                setDirectorySearchWhere('');
            }
            const parts = page.split('/');
            setCategorySlug(parts[1] || null);
            setArticleSlug(parts.length > 2 ? decodeURIComponent(parts[parts.length - 1]) : null);
            setCustomPageSlug(null);
        } else if (page === 'blog' && category && category !== 'all') {
            // Find category slug if category is a name
            let catSlug = category;
            const cat = newsCategories.find(c => c.name === category || c.slug === category);
            if (cat) catSlug = cat.slug;
            else catSlug = slugify(category);
            
            window.history.pushState({}, '', `${rootBaseClean}${encodeURIComponent(catSlug)}`);
            setPageParam('blog');
            setCategorySlug(catSlug);
            setArticleSlug(null);
            setCustomPageSlug(null);
        } else if (page.startsWith('p/')) {
            window.history.pushState({}, '', `${rootBaseClean}${page}`);
            setPageParam(page);
            setCategorySlug(null);
            setArticleSlug(null);
            setCustomPageSlug(page.replace('p/', ''));
        } else if (page.startsWith('bio/')) {
            window.history.pushState({}, '', `${rootBaseClean}${page}`);
            setPageParam('bio');
            setBioLinkSlug(page.replace('bio/', ''));
            setCategorySlug(null);
            setArticleSlug(null);
            setCustomPageSlug(null);
        } else {
            const knownPages = ['home', 'produtos', 'contato', 'sobre', 'servicos', 'blog', 'search', 'quem-somos', 'privacidade', 'termos'];
            if (knownPages.includes(page)) {
                const base = getBasePath();
                const cleanBase = base.replace(/^\//, '').replace(/\/$/, '');
                
                if (page === 'home') {
                    window.history.pushState({}, '', rootBaseClean);
                } else {
                    window.history.pushState({}, '', `${rootBaseClean}${page}`);
                }
            } else {
                let search = `?pagina=${page}`;
                window.history.pushState({}, '', `${rootBaseClean}${search}`);
            }
            setPageParam(page);
            setCategorySlug(null);
            setArticleSlug(null);
            setCustomPageSlug(null);
        }
        setCurrentPath(window.location.pathname);
        window.dispatchEvent(new PopStateEvent('popstate'));
        window.scrollTo(0, 0);
    };

    const handleAdminNavigate = (tab: string) => {
        const base = getBasePath();
        const rootBase = base.startsWith('/') ? base : `/${base}`;
        const rootBaseClean = rootBase.endsWith('/') ? rootBase : `${rootBase}/`;
        window.history.pushState({}, '', `${rootBaseClean}admin/${tab}`);
        setPageParam('admin');
        setAdminTab(tab);
        setCurrentPath(window.location.pathname);
        window.scrollTo(0, 0);
    };

    const handleArticleNavigate = (slug: string, categoryNameOrSlug?: string) => {
        const url = getArticleUrl(slug, categoryNameOrSlug);
        window.history.pushState({}, '', url);
        
        // Update state
        setPageParam('blog');
        setArticleSlug(slug);
        
        // Find category slug
        let catSlug = 'geral';
        if (categoryNameOrSlug) {
            const cat = newsCategories.find(c => c.name === categoryNameOrSlug || c.slug === categoryNameOrSlug);
            if (cat) {
                catSlug = cat.slug;
            } else {
                catSlug = slugify(categoryNameOrSlug);
            }
        }
        setCategorySlug(catSlug);
        setCustomPageSlug(null);
        setCurrentPath(window.location.pathname);
        window.scrollTo(0, 0);
    };

    useEffect(() => {
        if (currentUser) { 
            setIsAdmin(currentUser.role === 'admin'); 
            try {
                localStorage.setItem('admin_user', JSON.stringify(currentUser));
            } catch (e) {
                console.error("Failed to save user to localStorage:", e);
            }
        } else {
            try {
                localStorage.removeItem('admin_user');
            } catch (e) {
                console.error("Failed to remove user from localStorage:", e);
            }
        }
    }, [currentUser]);

    useEffect(() => {
        const root = document.documentElement;
        const color = settings.primaryColor || '#2563eb';
        root.style.setProperty('--color-primary', color);
        root.style.setProperty('--color-primary-dark', color); 
    }, [settings.primaryColor]);

    const [isScrolled, setIsScrolled] = useState(false);
    const mainRef = React.useRef<HTMLDivElement>(null);

    const handleMainScroll = () => {
        if (mainRef.current) {
            setIsScrolled(mainRef.current.scrollTop > 20);
        }
    };

    useEffect(() => {
        const main = mainRef.current;
        if (main) {
            main.addEventListener('scroll', handleMainScroll);
            return () => main.removeEventListener('scroll', handleMainScroll);
        }
    }, [mainRef.current]);

    const populateStatesFromData = useCallback((data: any) => {
        if (!data) return;
        if (data.products) setProducts(data.products);
        if (data.banners) setBanners(data.banners);
        if (data.popups) setPopups(data.popups);
        if (data.tips) setTips(data.tips);
        if (data.subscribers) setSubscribers(data.subscribers);
        if (data.plans) setPlans(data.plans);
        if (data.subscriptions) setSubscriptions(data.subscriptions);
        if (data.businesses) setBusinesses(data.businesses);
        if (data.users) setUsers(data.users);
        if (data.customPages) setCustomPages(data.customPages);
        if (data.bioLinks) setBioLinks(data.bioLinks);
        if (data.informativeCards) setInformativeCards(data.informativeCards);
        if (data.scriptAds) setScriptAds(data.scriptAds);

        if (data.news) {
            const normalizedNews = data.news.map((n: any) => {
                ['title', 'excerpt', 'content'].forEach(field => {
                    if (n[field] && typeof n[field] === 'object' && n[field].text) {
                        n[field] = n[field].text;
                    }
                });
                if (n.layout_type === 'recipe' && n.recipe_data) {
                    if (typeof n.recipe_data === 'string') {
                        try {
                            n.recipe_data = JSON.parse(n.recipe_data);
                        } catch (e) {}
                    }
                    if (n.recipe_data && typeof n.recipe_data === 'object') {
                        const rd = n.recipe_data;
                        if (rd.ingredients && Array.isArray(rd.ingredients)) {
                            rd.ingredients = rd.ingredients.map((sec: any) => ({
                                ...sec,
                                title: sec.title && typeof sec.title === 'object' ? sec.title.text : (sec.sectionTitle && typeof sec.sectionTitle === 'object' ? sec.sectionTitle.text : (sec.title || sec.sectionTitle)),
                                items: Array.isArray(sec.items) ? sec.items.map((item: any) => {
                                    if (typeof item === 'string') {
                                        return { id: Math.random().toString(36).substr(2, 9), text: item };
                                    }
                                    let text = item.text;
                                    if (item.text && typeof item.text === 'object' && item.text.text) {
                                        text = item.text.text;
                                    } else if (!item.text && (item.amount || item.name)) {
                                        text = `${item.amount || ''} ${item.unit || ''} ${item.name || ''}`.trim();
                                    }
                                    return { 
                                        ...item, 
                                        id: item.id || Math.random().toString(36).substr(2, 9),
                                        text: text || '' 
                                    };
                                }) : []
                            }));
                        }
                        if (rd.instructions && Array.isArray(rd.instructions)) {
                            rd.instructions = rd.instructions.map((step: any) => {
                                let content = step.content;
                                if (step.content && typeof step.content === 'object' && step.content.text) {
                                    content = step.content.text;
                                } else if (!step.content && step.text) {
                                    content = typeof step.text === 'object' ? step.text.text : step.text;
                                }
                                return {
                                    ...step,
                                    title: step.title && typeof step.title === 'object' ? step.title.text : step.title,
                                    content: content || ''
                                };
                            });
                        }
                        if (rd.chefTips && Array.isArray(rd.chefTips)) {
                            rd.chefTips = rd.chefTips.map((tip: any) => {
                                if (typeof tip === 'string') return tip;
                                if (tip && typeof tip === 'object' && tip.text) return tip.text;
                                return String(tip);
                            });
                        }
                    }
                }
                if (n.layout_type === 'bible_study' && n.bible_study_data) {
                    if (typeof n.bible_study_data === 'string') {
                        try {
                            n.bible_study_data = JSON.parse(n.bible_study_data);
                        } catch (e) {}
                    }
                }
                return n;
            });
            setNews(normalizedNews);
        }

        if (data.authorProfile) setAuthorProfile(data.authorProfile);
        if (data.newsCategories) setNewsCategories((data.newsCategories || []).map((c: any) => ({ ...c, id: String(c.id), parentId: c.parentId ? String(c.parentId) : null })));
        if (data.reviewCategories) setReviewCategories((data.reviewCategories || []).map((c: any) => ({ ...c, id: String(c.id) })));
        if (data.guideCategories) setGuideCategories((data.guideCategories || []).map((c: any) => ({ ...c, id: String(c.id) })));
        if (data.productCategories) setProductCategories((data.productCategories || []).map((c: any) => ({ ...c, id: String(c.id) })));

        if (data.users && currentUser) {
            const updatedMe = data.users.find((u: User) => u.id == currentUser.id || u.username === currentUser.username);
            if (updatedMe) {
                setCurrentUser(prev => {
                    if (JSON.stringify(prev) !== JSON.stringify(updatedMe)) {
                        return updatedMe;
                    }
                    return prev;
                });
            }
        }

        if (data.settings) {
            const cleanSettings = { ...data.settings };
            if (cleanSettings.installationUrl) {
                cleanSettings.installationUrl = cleanSettings.installationUrl.replace(/\/admin(?:\/.*)?$/, '').replace(/\/$/, '');
                if (!cleanSettings.installationUrl.endsWith('/')) {
                    cleanSettings.installationUrl += '/';
                }
            }
            if (cleanSettings.enableDirectory === undefined) {
                cleanSettings.enableDirectory = true;
            }
            if (cleanSettings.directoryAsHomePage === undefined) {
                cleanSettings.directoryAsHomePage = false;
            }
            if (!cleanSettings.aiReviewPrompt) {
                cleanSettings.aiReviewPrompt = '';
            }
            setSettings(prev => ({ ...prev, ...cleanSettings }));
            if (data.settings.basePath) {
                const cleanBasePath = data.settings.basePath.replace(/\/admin(?:\/.*)?$/, '').replace(/\/$/, '');
                localStorage.setItem('site_base_path', cleanBasePath);
            }
        }
    }, [currentUser]);

    const fetchData = useCallback(async (token?: string) => {
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        };
        const activeToken = token || adminToken;
        if (activeToken) {
          headers['X-Admin-Token'] = activeToken;
        }

        const response = await fetch(`${API_URL}?t=${Date.now()}`, {
          headers
        }); 
        if (!response.ok) {
            if (response.status === 403) {
                throw new Error("Licença ou Acesso Negado (403). Verifique se o arquivo 'api/license.php' foi gerado e enviado para a sua hospedagem.");
            }
            throw new Error(`Falha na API: ${response.status}`);
        }
        
        const text = await response.text();
        if (!text || text.trim() === "") {
            throw new Error("Resposta da API está vazia. Certifique-se de que o arquivo 'api/config.php' possui as credenciais de banco de dados corretas e acesse 'www.litoralutil.com/api/migrate_all.php' para criar/migrar as tabelas.");
        }
        
        let data;
        try {
            data = JSON.parse(text);
        } catch (e) {
            console.error("JSON parse error from API content:", text);
            throw new Error("A API não retornou um JSON válido. Pode conter erros ou avisos do PHP. Acesse a API diretamente no navegador para depurar.");
        }
        
        if (data.error) throw new Error(data.error);

        // Populate in-memory state values
        populateStatesFromData(data);

        // Cache globally to load instantaneously on next renders/audits
        try {
            localStorage.setItem('cached_api_data', JSON.stringify(data));
        } catch (e) {
            console.error("Failed to commit api cache:", e);
        }

        if (activeToken) {
            try {
                const affRes = await fetch(`${API_URL}?action=get_affiliate_links`, { headers: { 'X-Admin-Token': activeToken } });
                if (affRes.ok) {
                    const affData = await affRes.json();
                    setAffiliateLinks(affData);
                }
            } catch (e) {
                console.error("Failed to fetch affiliate links", e);
            }
        }
        
        setApiError(null);
      } catch (error: any) {
        console.error("Fetch Error:", error);
        setApiError(`Offline: ${error.message}`);
      } finally {
        setLoading(false);
        hasInitialLoadFinished.current = true;
      }
    }, [adminToken, populateStatesFromData]);

    // Fast-loading hydration from Cache on mount
    useEffect(() => {
        try {
            const cached = localStorage.getItem('cached_api_data');
            if (cached) {
                const parsed = JSON.parse(cached);
                populateStatesFromData(parsed);
            }
        } catch (e) {
            console.error("Failed to hydrate from cache:", e);
        }
    }, [populateStatesFromData]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleUpdateSettings = async (newSettings: SiteSettings) => {
        setSettings(newSettings);
        setSaveStatus('saving');
        try {
            const res = await fetch(`${API_URL}?action=save_settings_atomic`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken || '' },
                body: JSON.stringify(newSettings)
            });
            if (!res.ok) {
                setSaveStatus('error');
                return false;
            }
            const result = await res.json();
            if (result.success) {
                setSaveStatus('saved');
                return true;
            } else {
                setSaveStatus('error');
                return false;
            }
        } catch (e) { 
            console.error("Erro ao salvar configurações:", e);
            setSaveStatus('error');
            return false;
        }
    };

    const handleSubmitNews = async (data: NewsFormData, editingId?: string) => {
        const tempId = editingId || data.id || `art-${Date.now()}`;
        
        // Find existing item if it's an update to preserve date and views
        const existingItem = news.find(n => n.id === editingId);
        
        const newItem: NewsItem = {
            ...data, 
            id: tempId,
            date: existingItem ? existingItem.date : new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
            views: existingItem ? existingItem.views : 0
        };
        
        // Optimistic update
        if (editingId) { 
            setNews(prev => prev.map(n => n.id === editingId ? { ...n, ...data } : n)); 
        } else { 
            setNews(prev => [newItem, ...prev]); 
        }
        
        try {
            const res = await fetch(`${API_URL}?action=save_news_item`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
                body: JSON.stringify(newItem)
            });
            const result = await res.json();
            if (result.success) {
                if (result.id && result.id !== tempId) {
                    setNews(prev => prev.map(n => n.id === tempId ? { ...n, id: result.id.toString() } : n));
                }
            } else {
                // Rollback or notify
                alert(`Erro ao salvar: ${result.error || 'Erro desconhecido'}`);
                fetchData(); // Refresh to sync with server
            }
        } catch (e: any) { 
            console.error("Falha SQL", e); 
            alert(`Erro de conexão: ${e.message}`);
            fetchData(); // Refresh to sync with server
        }
    };

    const handleBulkSubmitNews = async (items: NewsFormData[]) => {
        const newItems: NewsItem[] = items.map(item => ({
            ...item, id: item.id || `art-${Date.now()}-${Math.random()}`,
            date: new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
            views: 0,
        }));
        setNews(prev => [...newItems, ...prev]);
        
        let errors = 0;
        for (const item of newItems) {
            try {
                const res = await fetch(`${API_URL}?action=save_news_item`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
                    body: JSON.stringify(item)
                });
                const result = await res.json();
                if (result.success) {
                    if (result.id && result.id !== item.id) {
                        setNews(prev => prev.map(n => n.id === item.id ? { ...n, id: result.id.toString() } : n));
                    }
                } else {
                    console.error("Erro ao salvar item em massa:", result.error);
                    errors++;
                }
            } catch (e) { 
                console.error("Falha SQL bulk", e); 
                errors++;
            }
        }
        
        if (errors > 0) {
            alert(`Ocorreram erros ao salvar ${errors} matérias. Verifique o console ou atualize a página.`);
            fetchData();
        }
    };

    const handleDeleteNews = async (id: string) => {
        setNews(prev => prev.filter(n => n.id !== id));
        try { await fetch(`${API_URL}?action=delete_news_item`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken }, body: JSON.stringify({ id }) }); } catch (e) {}
    };

    const handleAddNewsCategory = async (name: string, parentId?: string) => {
        const slug = slugify(name);
        setSaveStatus('saving');
        try {
            const res = await fetch(`${API_URL}?action=save_category`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
                body: JSON.stringify({ name, slug, parentId })
            });
            const result = await res.json();
            if (result.success && result.category) {
                setNewsCategories(prev => [...prev, { 
                    id: String(result.category.id), 
                    name: result.category.name, 
                    slug: result.category.slug, 
                    parentId: result.category.parentId ? String(result.category.parentId) : null 
                }]);
                setSaveStatus('saved');
            } else {
                setSaveStatus('error');
                alert(result.error || "Erro ao adicionar categoria");
            }
        } catch (e) { 
            setSaveStatus('error'); 
            alert("Erro de conexão ao adicionar categoria");
        }
    };

    const handleUpdateNewsCategory = async (id: string, name: string, parentId?: string) => {
        const slug = slugify(name);
        setSaveStatus('saving');
        try {
            const res = await fetch(`${API_URL}?action=save_category`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
                body: JSON.stringify({ id, name, slug, parentId })
            });
            const result = await res.json();
            if (result.success && result.category) {
                setNewsCategories(prev => prev.map(c => c.id === id ? { 
                    ...c, 
                    name: result.category.name, 
                    slug: result.category.slug, 
                    parentId: result.category.parentId ? String(result.category.parentId) : null 
                } : c));
                setSaveStatus('saved');
            } else {
                setSaveStatus('error');
                alert(result.error || "Erro ao atualizar categoria");
            }
        } catch (e) { 
            setSaveStatus('error'); 
            alert("Erro de conexão ao atualizar categoria");
        }
    };

    const handleDeleteNewsCategory = async (id: string) => {
        setSaveStatus('saving');
        try {
            await fetch(`${API_URL}?action=delete_category`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
                body: JSON.stringify({ id })
            });
            setNewsCategories(prev => prev.filter(c => c.id !== id));
            setSaveStatus('saved');
        } catch (e) { setSaveStatus('error'); }
    };

    const handleAddReviewCategory = async (name: string, parentId?: string) => {
        const slug = slugify(name);
        setSaveStatus('saving');
        try {
            const res = await fetch(`${API_URL}?action=save_review_category`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
                body: JSON.stringify({ name, slug, parentId })
            });
            const result = await res.json();
            if (result.success && result.category) {
                setReviewCategories(prev => [...prev, { 
                    id: String(result.category.id), 
                    name: result.category.name, 
                    slug: result.category.slug,
                    parentId: result.category.parentId
                }]);
                setSaveStatus('saved');
            } else {
                setSaveStatus('error');
                alert(result.error || "Erro ao salvar categoria");
            }
        } catch (e) { 
            setSaveStatus('error');
            alert("Erro de conexão ao salvar categoria");
        }
    };

    const handleUpdateReviewCategory = async (id: string, name: string, parentId?: string) => {
        const slug = slugify(name);
        setSaveStatus('saving');
        try {
            const res = await fetch(`${API_URL}?action=save_review_category`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
                body: JSON.stringify({ id, name, slug, parentId })
            });
            const result = await res.json();
            if (result.success) {
                setReviewCategories(prev => prev.map(c => c.id === id ? { ...c, name, slug, parentId } : c));
                setSaveStatus('saved');
            } else {
                setSaveStatus('error');
                alert(result.error || "Erro ao atualizar categoria");
            }
        } catch (e) { 
            setSaveStatus('error');
            alert("Erro de conexão ao atualizar categoria");
        }
    };

    const handleDeleteReviewCategory = async (id: string) => {
        setSaveStatus('saving');
        try {
            const res = await fetch(`${API_URL}?action=delete_review_category`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
                body: JSON.stringify({ id })
            });
            const result = await res.json();
            if (result.success) {
                setReviewCategories(prev => prev.filter(c => c.id !== id));
                setSaveStatus('saved');
            } else {
                setSaveStatus('error');
                alert(result.error || "Erro ao excluir categoria");
            }
        } catch (e) { 
            setSaveStatus('error');
            alert("Erro de conexão ao excluir categoria");
        }
    };

    const handleAddGuideCategory = async (name: string, parentId?: string) => {
        const slug = slugify(name);
        setSaveStatus('saving');
        try {
            const res = await fetch(`${API_URL}?action=save_guide_category`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
                body: JSON.stringify({ name, slug, parentId })
            });
            const result = await res.json();
            if (result.success && result.category) {
                setGuideCategories(prev => [...prev, { 
                    id: String(result.category.id), 
                    name: result.category.name, 
                    slug: result.category.slug,
                    parentId: result.category.parentId
                }]);
                setSaveStatus('saved');
            } else {
                setSaveStatus('error');
                alert(result.error || "Erro ao salvar categoria do guia");
            }
        } catch (e) { 
            setSaveStatus('error');
            alert("Erro de conexão ao salvar categoria do guia");
        }
    };

    const handleUpdateGuideCategory = async (id: string, name: string, parentId?: string) => {
        const slug = slugify(name);
        setSaveStatus('saving');
        try {
            const res = await fetch(`${API_URL}?action=save_guide_category`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
                body: JSON.stringify({ id, name, slug, parentId })
            });
            const result = await res.json();
            if (result.success) {
                setGuideCategories(prev => prev.map(c => c.id === id ? { ...c, name, slug, parentId } : c));
                setSaveStatus('saved');
            } else {
                setSaveStatus('error');
                alert(result.error || "Erro ao atualizar categoria do guia");
            }
        } catch (e) { 
            setSaveStatus('error');
            alert("Erro de conexão ao atualizar categoria do guia");
        }
    };

    const handleDeleteGuideCategory = async (id: string) => {
        setSaveStatus('saving');
        try {
            const res = await fetch(`${API_URL}?action=delete_guide_category`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
                body: JSON.stringify({ id })
            });
            const result = await res.json();
            if (result.success) {
                setGuideCategories(prev => prev.filter(c => c.id !== id));
                setSaveStatus('saved');
            } else {
                setSaveStatus('error');
                alert(result.error || "Erro ao excluir categoria");
            }
        } catch (e) {
            setSaveStatus('error');
            alert("Erro de conexão ao excluir categoria");
        }
    };

    const handleLogin = async (creds: any) => {
        // Bypass for preview environment
        if (window.location.hostname.includes('run.app') || window.location.hostname.includes('localhost')) {
            if (creds.username === 'admin' && creds.password === 'admin') {
                const mockUser: any = { id: 1, name: 'Admin (Preview)', username: 'admin', role: 'admin', email: 'admin@preview.com', referral_code: 'REF-ADMIN' };
                setCurrentUser(mockUser);
                setAdminToken('mock-token');
                setIsAdmin(true);
                localStorage.setItem('admin_token', 'mock-token');
                localStorage.setItem('admin_user', JSON.stringify(mockUser));
                setIsAuthModalOpen(false);
                handleNavigate('admin');
                return;
            }
        }

        const res = await fetch(`${API_URL}?action=login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(creds) });
        const data = await res.json();
        if (data.success) {
            setAdminToken(data.token); 
            setCurrentUser(data.user); 
            setIsAdmin(data.user.role === 'admin');
            if (data.user.role === 'admin') setAuthorProfile(data.user);
            
            localStorage.setItem('admin_token', data.token); 
            localStorage.setItem('admin_user', JSON.stringify(data.user));
            setIsAuthModalOpen(false); 
            
            // Recarrega dados com o novo token para pegar as configurações de admin
            fetchData(data.token);
            
            handleNavigate('admin');
        } else { throw new Error(data.error); }
    };

    const handleRegister = async (regForm: any) => {
        const refCodeInStorage = localStorage.getItem('pending_referral_code') || '';
        const payload = {
            username: regForm.username,
            password: regForm.password,
            name: regForm.name,
            email: regForm.email,
            phone: regForm.phone,
            referral_code: refCodeInStorage
        };
        const res = await fetch(`${API_URL}?action=register_user`, { 
            method: 'POST', 
            headers: { 'Content-Type': 'application/json' }, 
            body: JSON.stringify(payload) 
        });
        const data = await res.json();
        if (data.success) {
            setAdminToken(data.token);
            setCurrentUser(data.user);
            setIsAdmin(data.user.role === 'admin');
            setIsAuthModalOpen(false);
            localStorage.setItem('admin_token', data.token);
            localStorage.setItem('admin_user', JSON.stringify(data.user));
            localStorage.removeItem('pending_referral_code');
            
            fetchData(data.token);
            handleNavigate('admin');
        } else {
            throw new Error(data.error || 'Erro ao registrar usuário.');
        }
    };

    const handleDownloadBackup = async () => {
        try {
            const response = await fetch(`${API_URL}?action=download_backup`, {
                headers: { 'X-Admin-Token': adminToken }
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `backup_${new Date().toISOString().split('T')[0]}.sql`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                alert("Erro ao baixar backup");
            }
        } catch (e) {
            alert("Erro de conexão ao baixar backup");
        }
    };

    const handleSubscribe = async (data: Omit<Subscriber, 'id' | 'date'>) => {
        try {
            const response = await fetch(`${API_URL}?action=save_subscriber`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            
            if (!response.ok) throw new Error('Falha ao salvar');
            
            const newSubscriber: Subscriber = {
                ...data,
                id: `sub-${Date.now()}`,
                date: new Date().toLocaleDateString('pt-BR')
            };
            setSubscribers(prev => [newSubscriber, ...prev]);
            return true;
        } catch (e) {
            console.error("Erro ao salvar inscrito", e);
            return false;
        }
    };

    const [loadTimeout, setLoadTimeout] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (loading) setLoadTimeout(true);
        }, 15000); // 15 seconds
        return () => clearTimeout(timer);
    }, [loading]);

    // Force blocking fullscreen loader ONLY for the private Admin Panel or if the load times out completely (API/DB offline)
    if (loading && (pageParam === 'admin' || loadTimeout)) return (
        <div className="fixed inset-0 bg-white flex flex-col items-center justify-center p-6 text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-6"></div>
            <p className="text-gray-900 font-black uppercase tracking-tighter text-xl">Inicializando...</p>
            <p className="text-gray-400 text-sm mt-1 max-w-xs font-semibold text-slate-400">Carregando portal de alta performance...</p>
            
            {loadTimeout && (
                <div className="mt-12 animate-fade-in">
                    <p className="text-red-500 font-bold mb-4">A inicialização está demorando mais que o esperado.</p>
                    <button 
                        onClick={() => window.location.reload()}
                        className="px-8 py-4 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl active:scale-95 transition-all"
                    >
                        Tentar Recarregar
                    </button>
                    <p className="mt-4 text-[10px] text-gray-400 font-bold">VERIFIQUE SUA CONEXÃO OU TABELAS DO BANCO DE DADOS</p>
                </div>
            )}
        </div>
    );

    return (
        <ErrorBoundary>
            <LicenseGuard>
                <Suspense fallback={
                    <div className="fixed inset-0 bg-white flex flex-col items-center justify-center p-6 text-center z-[9999]">
                        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-6"></div>
                        <p className="text-gray-900 font-bold uppercase tracking-tight text-lg">Carregando...</p>
                        <p className="text-gray-400 text-sm mt-1">Isso levará apenas alguns instantes.</p>
                    </div>
                }>
                    {pageParam === 'admin' && currentUser && adminToken ? (
                    <>
                        {apiError && <div className="bg-red-600 text-white p-4 text-center font-bold fixed top-0 left-0 right-0 z-[200] flex items-center justify-center gap-4"><AlertTriangle size={20} /> <span>{apiError}</span><button onClick={() => window.location.reload()} className="bg-white text-red-600 px-4 py-1.5 rounded-xl uppercase text-xs font-black">Recarregar</button></div>}
                        <Dashboard
                    currentTab={adminTab}
                    onTabChange={handleAdminNavigate}
                    products={products} news={news} newsCategories={newsCategories} subscribers={subscribers} banners={banners} tips={tips} popups={popups} settings={settings} users={users} currentUser={currentUser} adminToken={adminToken} apiUrl={API_URL} affiliateLinks={affiliateLinks} businesses={businesses} onUpdateBusinesses={setBusinesses} setAffiliateLinks={setAffiliateLinks}
                    bioLinks={bioLinks}
                    informativeCards={informativeCards}
                    scriptAds={scriptAds}
                    onSaveBioLink={async (data) => {
                        try {
                            const res = await fetch(`${API_URL}?action=save_bio_link`, {
                                method: 'POST',
                                headers: { 'X-Admin-Token': adminToken, 'Content-Type': 'application/json' },
                                body: JSON.stringify(data)
                            });
                            const result = await res.json();
                            if (result.success) {
                                fetchData();
                            }
                            return result;
                        } catch (e) {
                            return { success: false, error: 'Erro ao salvar Link na Bio' };
                        }
                    }}
                    onDeleteBioLink={async (id) => {
                        try {
                            const res = await fetch(`${API_URL}?action=delete_bio_link`, {
                                method: 'POST',
                                headers: { 'X-Admin-Token': adminToken, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ id })
                            });
                            const result = await res.json();
                            if (result.success) {
                                setBioLinks(prev => prev.filter(b => b.id !== id));
                            }
                        } catch (e) {}
                    }}
                    onSaveInformativeCard={async (data) => {
                        try {
                            const res = await fetch(`${API_URL}?action=save_informative_card`, {
                                method: 'POST',
                                headers: { 'X-Admin-Token': adminToken, 'Content-Type': 'application/json' },
                                body: JSON.stringify(data)
                            });
                            const result = await res.json();
                            if (result.success) {
                                fetchData();
                            }
                        } catch (e) {
                            console.error("Error saving informative card", e);
                        }
                    }}
                    onDeleteInformativeCard={async (id) => {
                        try {
                            const res = await fetch(`${API_URL}?action=delete_informative_card&id=${id}`, {
                                method: 'POST',
                                headers: { 'X-Admin-Token': adminToken }
                            });
                            const result = await res.json();
                            if (result.success) {
                                setInformativeCards(prev => prev.filter(c => c.id !== id));
                            }
                        } catch (e) {
                            console.error("Error deleting informative card", e);
                        }
                    }}
                    onSaveScriptAd={async (data) => {
                        try {
                            const res = await fetch(`${API_URL}?action=save_script_ad`, {
                                method: 'POST',
                                headers: { 'X-Admin-Token': adminToken, 'Content-Type': 'application/json' },
                                body: JSON.stringify(data)
                            });
                            const result = await res.json();
                            if (result.success) {
                                fetchData();
                            }
                        } catch (e) {
                            console.error("Error saving script ad", e);
                        }
                    }}
                    onDeleteScriptAd={async (id) => {
                        try {
                            const res = await fetch(`${API_URL}?action=delete_script_ad`, {
                                method: 'POST',
                                headers: { 'X-Admin-Token': adminToken, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ id })
                            });
                            const result = await res.json();
                            if (result.success) {
                                setScriptAds(prev => prev.filter(a => a.id !== id));
                            }
                        } catch (e) {
                            console.error("Error deleting script ad", e);
                        }
                    }}
                    onUpdateUser={(u) => {
                        setCurrentUser(u);
                        if (u.id == 1 || u.role === 'admin') setAuthorProfile(u);
                    }} onDeleteUser={async (id) => { if(id===1)return; try{ await fetch(`${API_URL}?action=delete_user`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({id})}); setUsers(prev=>prev.filter(u=>u.id!==id)); }catch(e){} }}
                    onUpdateUserRole={async (id, role) => { try{ await fetch(`${API_URL}?action=update_user_role`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({id, role})}); setUsers(prev=>prev.map(u=>u.id===id?{...u, role}:u)); }catch(e){} }}
                    onUpdateUserDetails={async (id, data) => { 
                        try{ 
                            await fetch(`${API_URL}?action=update_user`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({...data, id})}); 
                            setUsers(prev=>prev.map(u=>u.id===id?{...u,...data}:u)); 
                            if (id === 1 || (currentUser && id === currentUser.id)) {
                                setAuthorProfile(prev => prev ? {...prev, ...data} : null);
                            }
                        }catch(e){} 
                    }}
                    saveStatus={saveStatus} onAddProduct={() => { setEditingProduct(null); setIsAdminPanelOpen(true); }} onEditProduct={(p) => { setEditingProduct(p); setIsAdminPanelOpen(true); }}
                    onDeleteProduct={async (id) => { try{ await fetch(`${API_URL}?action=delete_product`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({id})}); setProducts(prev=>prev.filter(p=>p.id!==id)); }catch(e){} }}
                    onSubmitNews={handleSubmitNews} onBulkSubmitNews={handleBulkSubmitNews} onDeleteNews={handleDeleteNews} onAddNewsCategory={handleAddNewsCategory} onUpdateNewsCategory={handleUpdateNewsCategory} onDeleteNewsCategory={handleDeleteNewsCategory}
                    reviewCategories={reviewCategories} onAddReviewCategory={handleAddReviewCategory} onUpdateReviewCategory={handleUpdateReviewCategory} onDeleteReviewCategory={handleDeleteReviewCategory}
                    guideCategories={guideCategories} onAddGuideCategory={handleAddGuideCategory} onUpdateGuideCategory={handleUpdateGuideCategory} onDeleteGuideCategory={handleDeleteGuideCategory}
                    onSaveKeywordLink={async (data) => { 
                        try { 
                            const res = await fetch(`${API_URL}?action=save_keyword_link`, {
                                method: 'POST', 
                                headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken || '' }, 
                                body: JSON.stringify(data)
                            });
                            if (res.ok) {
                                const result = await res.json();
                                setSettings(prev => ({
                                    ...prev,
                                    keywordLinks: (data as any).id 
                                        ? prev.keywordLinks?.map(l => l.id === (data as any).id ? { ...l, ...data } as KeywordLink : l)
                                        : [{ ...data, id: result.id?.toString() || Date.now().toString() } as KeywordLink, ...(prev.keywordLinks || [])]
                                }));
                            }
                        } catch (e) {} 
                    }}
                    onDeleteKeywordLink={async (id) => { 
                        try { 
                            const res = await fetch(`${API_URL}?action=delete_keyword_link`, {
                                method: 'POST', 
                                headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken || '' }, 
                                body: JSON.stringify({ id })
                            });
                            if (res.ok) {
                                setSettings(prev => ({
                                    ...prev,
                                    keywordLinks: prev.keywordLinks?.filter(l => l.id !== id)
                                }));
                            }
                        } catch (e) {} 
                    }}
                    onSaveInternalLinkLog={async (data) => { try{ await fetch(`${API_URL}?action=save_internal_link_log`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify(data)}); }catch(e){} }}
                    onDeleteInternalLinkLog={async (id) => { try{ await fetch(`${API_URL}?action=delete_internal_link_log`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({id})}); }catch(e){} }}
                    onDeleteSubscriber={async (id) => { try{ await fetch(`${API_URL}?action=delete_subscriber`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({id})}); setSubscribers(prev=>prev.filter(s=>s.id!==id)); }catch(e){} }}
                    onUpdateSubscriber={(id, data) => {}}
                    onAddBanner={async (d) => { try{ const res = await fetch(`${API_URL}?action=save_banner`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify(d)}); const result = await res.json(); setBanners(prev=>[{...d, id:result.id?.toString() || Date.now().toString(), clicks:0, views:0}, ...prev]); }catch(e){} }}
                    onEditBanner={async (id, d) => { try{ await fetch(`${API_URL}?action=save_banner`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({...d, id})}); setBanners(prev=>prev.map(b=>b.id===id?{...b,...d}:b)); }catch(e){} }}
                    onDeleteBanner={async (id) => { try{ await fetch(`${API_URL}?action=delete_banner`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({id})}); setBanners(prev=>prev.filter(b=>b.id!==id)); }catch(e){} }}
                    onAddTip={async (d) => { try{ const res = await fetch(`${API_URL}?action=save_tip`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify(d)}); const result = await res.json(); setTips(prev=>[{...d, id:result.id?.toString() || Date.now().toString()}, ...prev]); }catch(e){} }}
                    onEditTip={async (id, d) => { try{ await fetch(`${API_URL}?action=save_tip`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({...d, id})}); setTips(prev=>prev.map(t=>t.id===id?{...t,...d}:t)); }catch(e){} }}
                    onDeleteTip={async (id) => { try{ await fetch(`${API_URL}?action=delete_tip`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({id})}); setTips(prev=>prev.filter(t=>t.id!==id)); }catch(e){} }}
                    onAddPopup={async (d) => { try{ const res = await fetch(`${API_URL}?action=save_popup`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify(d)}); const result = await res.json(); setPopups(prev=>[{...d, id:result.id?.toString() || Date.now().toString()}, ...prev]); }catch(e){} }}
                    onEditPopup={async (id, d) => { try{ await fetch(`${API_URL}?action=save_popup`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({...d, id})}); setPopups(prev=>prev.map(p=>p.id===id?{...p,...d}:p)); }catch(e){} }}
                    onDeletePopup={async (id) => { try{ await fetch(`${API_URL}?action=delete_popup`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({id})}); setPopups(prev=>prev.filter(p=>p.id!==id)); }catch(e){} }}
                    onUpdateSettings={handleUpdateSettings}
                    onDownloadBackup={handleDownloadBackup}
                    productCategories={productCategories}
                    onAddProductCategory={async (name, parentId) => {
                        try {
                            const res = await fetch(`${API_URL}?action=save_product_category`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({name, parentId})});
                            const result = await res.json();
                            if (result.success && result.category) {
                                setProductCategories(prev => [...prev, {
                                    id: result.category.id.toString(), 
                                    name: result.category.name, 
                                    slug: result.category.slug,
                                    parentId: result.category.parentId
                                }]);
                            }
                        } catch(e){}
                    }}
                    onUpdateProductCategory={async (id, name, parentId) => {
                        try {
                            const res = await fetch(`${API_URL}?action=save_product_category`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({id, name, parentId})});
                            const result = await res.json();
                            if (result.success) {
                                setProductCategories(prev => prev.map(c => c.id === id ? {...c, name, parentId} : c));
                            }
                        } catch(e){}
                    }}
                    onDeleteProductCategory={async (id) => {
                        try {
                            const res = await fetch(`${API_URL}?action=delete_product_category`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify({id})});
                            const result = await res.json();
                            if (result.success) {
                                setProductCategories(prev => prev.filter(c => c.id !== id));
                            }
                        } catch(e){}
                    }}
                    onExit={() => { localStorage.clear(); window.location.href = getBasePath() || '/'; }}
                />
                <AdminPanel 
                    isOpen={isAdminPanelOpen} 
                    onClose={() => setIsAdminPanelOpen(false)} 
                    onSubmit={async (data) => {
                        const tempId = editingProduct?.id || `temp-${Date.now()}`;
                        const newItem = {...data, id: tempId};
                        try { 
                            const res = await fetch(`${API_URL}?action=save_product`, {method:'POST', headers:{'X-Admin-Token':adminToken}, body:JSON.stringify(newItem)}); 
                            const result = await res.json();
                            if (result.success && result.id && result.id !== tempId) {
                                newItem.id = result.id.toString();
                            }
                            setProducts(prev => editingProduct ? prev.map(p=>p.id===editingProduct.id?newItem:p) : [newItem, ...prev]); 
                            setIsAdminPanelOpen(false); 
                        } catch(e){}
                    }} 
                    editingProduct={editingProduct} 
                    settings={settings} 
                    categories={reviewCategories} 
                    productCategories={productCategories}
                />
            </>
            ) : (
                <div className="bg-white min-h-screen flex flex-col pb-24 lg:pb-0">
                    {loading && (
                        <div className="fixed top-0 left-0 right-0 h-[3px] bg-slate-100 z-[9999] overflow-hidden">
                            <div className="absolute h-full bg-primary animate-progress-indefinite"></div>
                        </div>
                    )}
                    {apiError && (
                        <div className="bg-red-600 text-white p-4 text-center font-bold sticky top-0 z-[100] flex items-center justify-center gap-4">
                            <AlertTriangle size={20} /> 
                            <span>{apiError}</span>
                            <button onClick={() => window.location.reload()} className="bg-white text-red-600 px-4 py-1.5 rounded-xl uppercase text-xs font-black">Recarregar</button>
                        </div>
                    )}
                    <AdBlockDetector settings={settings} />
                    <AdSenseScript settings={settings} currentPath={currentPath} />
                    <TrackingScripts settings={settings} />
                    <GlobalAdScript settings={settings} />
                    <Helmet 
                        pageParam={pageParam}
                        articleSlug={articleSlug}
                        productSlug={productSlug}
                        categorySlug={categorySlug}
                        customPageSlug={customPageSlug}
                        bioLinkSlug={bioLinkSlug}
                        storySlug={storySlug}
                        siteSlug={siteSlug}
                        settings={settings}
                        products={products}
                        news={news}
                        businesses={businesses}
                        customPages={customPages}
                        bioLinks={bioLinks}
                        newsCategories={newsCategories}
                        productCategories={productCategories}
                        guideCategories={guideCategories}
                    />
                    {pageParam !== 'bio' && (
                        <>
                            <Header 
                                settings={settings} 
                                currentPage={pageParam} 
                                onNavigate={handleNavigate} 
                                products={products} 
                                news={publicNews} 
                                onOpenAuth={() => setIsAuthModalOpen(true)} 
                                currentUser={currentUser} 
                                customPages={customPages}
                                isScrolled={isScrolled}
                                canInstallPwa={settings.pwaActive !== false && !isPwaInstalled}
                                onInstallPwa={handleInstallPwa}
                            />
                        </>
                    )}
                    <main 
                        ref={mainRef}
                        className="flex-1 flex flex-col relative"
                    >
                        {pageParam === 'site' && siteSlug ? (
                            <PublicSitePage slug={siteSlug} />
                        ) : pageParam === 'bio' && bioLinkSlug ? (
                            <BioLinkPage slug={bioLinkSlug} settings={settings} />
                        ) : pageParam === 'story' && storySlug ? (
                            <WebStoryViewer slug={storySlug} settings={settings} apiUrl={API_URL} onClose={() => handleNavigate('webstories')} />
                        ) : customPageSlug ? (
                            <CustomPageRenderer slug={customPageSlug} apiUrl={API_URL} initialPage={customPages.find(p => p.slug === customPageSlug)} />
                        ) : (pageParam === 'carrossel' || pageParam === 'carousel') ? (
                            <CarouselApp settings={settings} apiUrl={API_URL} adminToken={adminToken} />
                        ) : (pageParam === 'guia' || (pageParam === 'home' && settings.directoryAsHomePage)) && settings.enableDirectory ? (
                            articleSlug ? (
                                publicBusinesses.find(b => (b.slug && b.slug === articleSlug) || (b.id && b.id.toString() === articleSlug.toString())) ? (
                                    <BusinessDetailPage 
                                        business={publicBusinesses.find(b => (b.slug && b.slug === articleSlug) || (b.id && b.id.toString() === articleSlug.toString()))!} 
                                        settings={settings} 
                                        onNavigate={handleNavigate} 
                                        onNavigateToArticle={(a) => handleArticleNavigate(a.slug || a.id, a.category)}
                                        apiUrl={API_URL}
                                    />
                                ) : loading ? (
                                    <div className="flex items-center justify-center min-h-[50vh]">
                                        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                ) : <NotFoundPage brokenSlug={articleSlug} news={publicNews} products={products} onNavigateHome={() => handleNavigate('home')} onNavigateToArticle={(a) => handleArticleNavigate(a.slug || a.id, a.category)} />
                            ) : directorySearchWhat || directorySearchWhere ? (
                                <DirectorySearchResults
                                    settings={settings}
                                    businesses={publicBusinesses}
                                    guideCategories={guideCategories}
                                    onNavigate={handleNavigate}
                                    initialSearchWhat={directorySearchWhat}
                                    initialSearchWhere={directorySearchWhere}
                                    onClearSearch={() => {
                                        setDirectorySearchWhat('');
                                        setDirectorySearchWhere('');
                                        const base = getBasePath();
                                        const rootBase = base.startsWith('/') ? base : `/${base}`;
                                        const rootBaseClean = rootBase.endsWith('/') ? rootBase : `${rootBase}/`;
                                        window.history.pushState({}, '', `${rootBaseClean}guia`);
                                    }}
                                />
                            ) : (
                                <DirectoryPage 
                                    settings={settings} 
                                    businesses={publicBusinesses} 
                                    guideCategories={guideCategories}
                                    plans={plans}
                                    onNavigate={handleNavigate} 
                                    onSelectPlan={setSelectedPlan}
                                    onSearch={(what, where) => {
                                        setDirectorySearchWhat(what);
                                        setDirectorySearchWhere(where);
                                        const base = getBasePath();
                                        const rootBase = base.startsWith('/') ? base : `/${base}`;
                                        const rootBaseClean = rootBase.endsWith('/') ? rootBase : `${rootBase}/`;
                                        window.history.pushState({}, '', `${rootBaseClean}guia?q=${encodeURIComponent(what)}&loc=${encodeURIComponent(where)}`);
                                    }}
                                />
                            )
                        ) : articleSlug ? (
                            news.find(n => n.slug === articleSlug || n.id == articleSlug) ? (
                                <ArticlePage 
                                    article={news.find(n => n.slug === articleSlug || n.id == articleSlug)!} 
                                    relatedArticles={publicNews.filter(n => {
                                        const isLocalSeo = n.is_local_seo === true || Number(n.is_local_seo) === 1 || String(n.is_local_seo) === '1';
                                        const hasLocalSeoTag = n.tags?.some(tag => {
                                            const t = tag.toLowerCase().trim();
                                            return t === 'local-seo' || t === 'seo-local' || t === 'seo local' || t === 'seo_local';
                                        });
                                        return (n.slug || n.id) !== articleSlug && !isLocalSeo && !hasLocalSeoTag;
                                    }).slice(0, 3)} 
                                    allNews={publicNews} 
                                    onBack={() => handleNavigate('blog')} 
                                    onNavigateToArticle={(a) => handleArticleNavigate(a.slug || a.id, a.category)} 
                                    onSubscribe={handleSubscribe as any} 
                                    settings={settings} 
                                    banners={banners} 
                                    tips={tips} 
                                    popups={popups} 
                                    currentUser={currentUser} 
                                    authorProfile={authorProfile as any}
                                    products={products}
                                    businesses={businesses}
                                    informativeCards={informativeCards}
                                    scriptAds={scriptAds}
                                    onCategoryClick={(cat) => handleNavigate('blog', cat)}
                                    apiUrl={API_URL} 
                                    adminToken={adminToken} 
                                    onOpenLogin={() => setIsAuthModalOpen(true)} 
                                />
                            ) : loading ? (
                                <div className="flex items-center justify-center min-h-[50vh]">
                                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            ) : <NotFoundPage brokenSlug={articleSlug} news={publicNews} products={products} onNavigateHome={() => handleNavigate('home')} onNavigateToArticle={(a) => handleArticleNavigate(a.slug || a.id, a.category)} />
                        ) : pageParam === 'webstories' ? (
                            <WebStoriesPage settings={settings} apiUrl={API_URL} onBack={() => handleNavigate('home')} />
                        ) : pageParam === 'produtos' ? (
                            productSlug ? (
                                products.find(p => p.slug === productSlug || p.id == productSlug) ? (
                                    <ProductDetailPage 
                                        product={products.find(p => p.slug === productSlug || p.id == productSlug)!}
                                        settings={settings}
                                        onBack={() => handleNavigate('produtos')}
                                        onNavigateToArticle={(a) => handleArticleNavigate(a.slug || a.id, a.category)}
                                        apiUrl={API_URL}
                                    />
                                ) : loading ? (
                                    <div className="flex items-center justify-center min-h-[50vh]">
                                        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                ) : <NotFoundPage brokenSlug={productSlug} news={publicNews} products={products} onNavigateHome={() => handleNavigate('home')} onNavigateToArticle={(a) => handleArticleNavigate(a.slug || a.id, a.category)} />
                            ) : (
                                <ProductsPage 
                                    products={products} 
                                    categories={productCategories}
                                    onBack={() => handleNavigate('home')} 
                                    onTrack={()=>{}} 
                                    onProductClick={handleProductClick}
                                    settings={settings} 
                                />
                            )
                        ) : pageParam === 'blog' ? (
                            <BlogPage news={publicNews} products={products} categories={publicCategories} settings={settings} authorProfile={authorProfile || users.find(u => u.role === 'admin')} onBack={() => handleNavigate('home')} onSubscribe={handleSubscribe} onReadArticle={(a) => handleArticleNavigate(a.slug || a.id, a.category)} onTrack={()=>{}} initialCategory={categorySlug || 'all'} />
                        ) : pageParam === 'quem-somos' ? <AboutPage settings={settings} onBack={() => handleNavigate('home')} />
                        : pageParam === 'beneficios' ? <BeneficiosPage settings={settings} currentUser={currentUser} onBack={() => handleNavigate('home')} onLoginRequest={() => setIsAuthModalOpen(true)} />
                        : pageParam === 'contato' ? <ContactPage settings={settings} onBack={() => handleNavigate('home')} />
                        : pageParam === 'privacidade' ? <LegalPage title={settings.privacyPageTitle || "Privacidade"} description={settings.privacyPageDescription || ""} content={settings.privacyPolicyContent || ""} onBack={() => handleNavigate('home')} />
                        : pageParam === 'termos' ? <LegalPage title={settings.termsPageTitle || "Termos"} description={settings.termsPageDescription || ""} content={settings.termsOfUseContent || ""} onBack={() => handleNavigate('home')} />
                        : pageParam === 'missao' ? <GamificationMissionViewer taskId={params.get('id')} adminToken={adminToken} apiUrl={API_URL} onClose={() => handleNavigate('admin')} />
                        : (
                            <>
                                <Hero 
                                    key={`hero-${products.length}-${settings.enableCustomHero}`}
                                    products={products} 
                                    settings={settings}
                                    isAdmin={false} 
                                    onEdit={() => {}} 
                                    onDelete={() => {}} 
                                    onTrack={()=>{}} 
                                    onClick={handleProductClick}
                                />
                                {products.length > 0 && (
                                    <div className="max-w-[1600px] mx-auto pt-4 pb-4 border-b border-gray-100">
                                        <ProductCarousel 
                                            title="Sistemas & Soluções Recentes" 
                                            products={products} 
                                            isAdmin={false} 
                                            onEdit={() => {}} 
                                            onDelete={() => {}} 
                                            onTrack={()=>{}} 
                                            onClick={handleProductClick}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </main>
                    {pageParam !== 'bio' && (
                        <>
                            <div className="max-w-7xl mx-auto px-4 md:px-6 pt-4 pb-12">
                                <NewsSection 
                                    news={publicNews} 
                                    categories={publicCategories} 
                                    settings={settings} 
                                    banners={banners} 
                                    onViewAll={() => handleNavigate('blog')} 
                                    onReadArticle={(a) => handleArticleNavigate(a.slug || a.id, a.category)} 
                                />
                            </div>
                            <Footer settings={settings} onNavigate={handleNavigate} onSubscribe={handleSubscribe} customPages={customPages} />
                            <MobileBottomNav currentPage={pageParam} onNavigate={handleNavigate} onOpenSearch={() => setIsSearchOpen(true)} onToggleChat={() => setIsChatWidgetOpen(!isChatWidgetOpen)} onOpenAuth={() => setIsAuthModalOpen(true)} currentUser={currentUser} isChatOpen={isChatWidgetOpen} />
                            <AiChatWidget 
                                settings={settings} 
                                news={publicNews} 
                                products={products} 
                                businesses={businesses}
                                currentArticle={articleSlug ? news.find(n => n.slug === articleSlug || n.id == articleSlug) : null} 
                                forceOpen={isChatWidgetOpen} 
                                onClose={() => setIsChatWidgetOpen(false)} 
                            />
                            <WhatsappFloatingButton settings={settings} />
                        </>
                    )}
                    {isAuthModalOpen && <AuthModal isOpen={true} onClose={() => setIsAuthModalOpen(false)} onLogin={handleLogin} onRegister={handleRegister} />}
                    {isSearchOpen && (
                        <AdvancedSearch 
                            items={publicNews} 
                            categories={publicCategories} 
                            isOpen={isSearchOpen} 
                            onClose={() => setIsSearchOpen(false)} 
                            onNavigate={(a) => handleArticleNavigate(a.slug || a.id, a.category)} 
                        />
                    )}
                    {popups.filter(p=>p.active).length > 0 && <PopupDisplay popup={popups.filter(p=>p.active)[0]} />}
                    <PwaHandler 
                        isStandalone={isPwaInstalled} 
                        canInstall={canInstallPwa} 
                        onInstall={handleInstallPwa} 
                    />
                    <Toaster position="top-right" richColors />
                    {selectedPlan && (
                        <CheckoutFlow 
                            plan={selectedPlan} 
                            currentUser={currentUser} 
                            businesses={businesses} 
                            settings={settings}
                            onClose={() => setSelectedPlan(null)} 
                            onOpenAuth={() => {
                                setSelectedPlan(null);
                                setIsAuthModalOpen(true);
                            }}
                        />
                    )}
                </div>
            )}
            </Suspense>
            </LicenseGuard>
        </ErrorBoundary>
    );
};

export default App;
